import isEmpty from 'lodash/isEmpty';
import { formatUrl, parseQuery } from 'url-lib';

import { getWindowObject } from '@eb/feature-detection';
import {
    setAttributes,
    trackEventFromState,
    trackInitialPageView,
} from '@eb/site-analytics';

import { getCanonicalSearchUrl } from '@eb/destination-utils';
import { trackInternalAppClick } from '../analytics';
import { logDiscoveryRequest } from '../api/analytics';

import {
    GA_SEARCH_CATEGORY,
    GA_ENTER_MAP_VIEW,
    SEARCH_EVENT_NAVIGATION,
    GA_PAGE_LOAD_ACTION,
    GOOGLE_OPTIMIZE_NULL_EXPERIMENT_ID,
} from '../constants';
import { getSearchId } from '../utils/analytics';

export const NAV_EXPERIMENT = '20q4navexperiment';

const MAX_EVENTS_FOR_GA = 10;
const processTagsForGA = (tagType, eventSearch) =>
    eventSearch.tags && eventSearch.tags.length
        ? eventSearch.tags.filter((tag) => tag.indexOf(tagType) > -1).join(',')
        : '';

const processDatesForCustomDimension = (eventSearch) => {
    if (
        eventSearch.dateRange &&
        eventSearch.dateRange.from &&
        eventSearch.dateRange.to
    ) {
        return `${eventSearch.dateRange.from},${eventSearch.dateRange.to}`;
    }

    if (Array.isArray(eventSearch.dates)) {
        return eventSearch.dates[1];
    }

    return '';
};

// There are some dimensions we want on all Search pageviews in GA
// The definitions come from here:
// https://analytics.google.com/analytics/web/#/a141520w171290p95297140/admin/custom-dimensions/m-content-dimensionsContent.rowShow=100&m-content-dimensionsContent.rowStart=0&m-content-dimensionsContent.sortColumnId=index&m-content-dimensionsContent.sortDescending=false
// The constants map to dimensions here:
// https://github.com/eventbrite/js-utils/blob/master/src/analytics/constants.js
export const getSearchCustomDimensionsAndMetrics = (
    eventSearch,
    slug,
    eventIds,
    entities,
) => {
    let dimensionsAndMetrics = {
        searchLocation: eventSearch.bbox || slug,
        // first half of events (due to GA action size limits, we can only send a max of 10 event IDs per dimension field)
        searchEventIds1: eventIds.slice(0, MAX_EVENTS_FOR_GA).join(','),
        // second half of events - right now we assume that there are 20 events on the page,
        // if we change it more, we will need more dimensions to accommodate it
        searchEventIds2: eventIds
            .slice(MAX_EVENTS_FOR_GA, MAX_EVENTS_FOR_GA * 2)
            .join(','),
        searchQuery: eventSearch.q,
        searchDates: processDatesForCustomDimension(eventSearch),
        searchPrice: eventSearch.price || '',
        searchCategory: processTagsForGA('EventbriteCategory', eventSearch),
        searchFormat: processTagsForGA('EventbriteFormat', eventSearch),
        searchSort: '',
        searchResultCount: eventIds.length,
        searchPagination: eventSearch.page,
    };

    // Don't track unattendable if we don't yet have the event sales status
    const shouldTrackUnattendableEventCount =
        eventIds.length && entities[eventIds[0]].eventSalesStatus;

    if (shouldTrackUnattendableEventCount) {
        const unattendableEventCount = eventIds
            .map((eventId) => entities[eventId])
            .reduce((prev, event) => {
                if (!event.eventSalesStatus) {
                    return undefined;
                }

                return event.eventSalesStatus.salesStatus === 'on_sale'
                    ? prev
                    : prev + 1;
            }, 0);

        dimensionsAndMetrics = {
            ...dimensionsAndMetrics,
            searchResultCountUnattendable: unattendableEventCount,
        };
    }

    return dimensionsAndMetrics;
};

export const trackEventClickInternal = (data) => (dispatch, getState) => {
    const {
        search: { searchId, eventSearch },
        app: { guestId },
    } = getState();

    const searchMetricId = getSearchId({ eventSearch, guestId });

    trackInternalAppClick({ searchId, searchMetricId, guestId, ...data });
};

export const removeNullExperiments = (experiments = {}) =>
    Object.keys(experiments).reduce(
        (prev, expName) =>
            experiments[expName] && experiments[expName] !== 'None'
                ? { ...prev, [expName]: experiments[expName] }
                : prev,
        {},
    );

const _getCurrentPath = (eventSearch, serverUrl, slug) => {
    const location = getWindowObject('location');

    let currentPath;

    if (location) {
        currentPath = location.href || '';
    } else {
        currentPath = '';
    }

    const canonicalSearchUrl = getCanonicalSearchUrl({
        ...eventSearch,
        slug,
    });

    const path = `${serverUrl}${canonicalSearchUrl}`;

    if (eventSearch.q) {
        const queryParams = currentPath.includes('?')
            ? parseQuery(currentPath)
            : {};

        currentPath = formatUrl(path, {
            ...queryParams,
            q: eventSearch.q,
        });
    }
    return currentPath;
};

/*
When a page is viewed there are several logs that are notified.
 */
export const trackPageView = (isBrowsePage) => (dispatch, getState) => {
    const {
        app: { guestId, publicId, serverUrl, experiments },
        gaSettings,
        search: { eventSearch },
        location: { slug },
        pagination: { currentPage, event },
        kompariExperimentId,
        auth: { isAuthenticated },
        content: { entities },
        launchNewNavigationHeader,
    } = getState();

    let gaDimensions = {};

    const eventIds = event[currentPage] ? event[currentPage].ids : [];

    const experimentsWithOptimize = {
        ...experiments,
        newSearch: window.google_optimize
            ? window.google_optimize.get(GOOGLE_OPTIMIZE_NULL_EXPERIMENT_ID)
            : null,
    };
    const cleanedExperiments = removeNullExperiments(experimentsWithOptimize);

    if (isBrowsePage) {
        if (kompariExperimentId) {
            gaDimensions.experimentId = kompariExperimentId;
        }
    } else {
        const searchId = logDiscoveryRequest({
            eventSearch,
            publicUserId: publicId,
            guestId,
            locationSlug: slug,
            //userGenerated is always unknown at this point in code
            // - did the user change the search or just click a SEO link?
            userGenerated: null,
            navigation: SEARCH_EVENT_NAVIGATION,
            action: GA_PAGE_LOAD_ACTION,
            // variant can be used to track experiments
            variant: JSON.stringify(cleanedExperiments),
            resultIds: eventIds,
            numResults: event[currentPage]
                ? event[currentPage].objectCount
                : null,
        });

        if (searchId) {
            gaDimensions.searchId = searchId;
        }

        gaDimensions = {
            ...gaDimensions,
            ...getSearchCustomDimensionsAndMetrics(
                eventSearch,
                slug,
                eventIds,
                entities,
            ),
        };
    }

    const currentPath = _getCurrentPath(eventSearch, serverUrl, slug);

    setAttributes({ location: currentPath });
    if (!isEmpty(cleanedExperiments)) {
        gaDimensions.experimentId = JSON.stringify(cleanedExperiments);
    }
    // Send this dimension along when the new header is displayed at the top of the page. The new header is
    // displayed when the launchNewNavigationHeader feature flag is on and the user is not authenticated
    if (launchNewNavigationHeader && !isAuthenticated) {
        gaDimensions.experimentId3 = NAV_EXPERIMENT;
    }
    trackInitialPageView(gaSettings, { dimensions: gaDimensions });
};

export const trackEnterMapView = () => (dispatch, getState) => {
    const state = getState();
    const {
        location: { location },
    } = state;

    trackEventFromState(state, {
        label: location,
        category: GA_SEARCH_CATEGORY,
        action: GA_ENTER_MAP_VIEW,
    });
};
